import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import ReportFilters, { formatReportMoney } from './ReportFilters';
import { getRevenueReport, ReportQuery, RevenueReport } from '../../api/reportsService';

const RevenueReportPage = () => {
  const [query, setQuery] = useState<ReportQuery>({ period: '1m' });
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getRevenueReport(query).then((data) => {
      if (!data) setError('Could not load revenue report.');
      setReport(data);
      setLoading(false);
    });
  }, [query]);

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/reports" className="text-sm text-torch-primary hover:underline">← Reports</Link>
        <h1 className="mt-1 text-2xl font-semibold text-gray-900">Revenue</h1>
        <p className="text-sm text-gray-500">Signed proposals and paid magazine invoices.</p>
      </div>

      <PageCard className="mb-6">
        <ReportFilters query={query} onChange={setQuery} showStatus={false} />
      </PageCard>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {loading ? (
        <PageCard><p className="text-sm text-gray-500">Loading…</p></PageCard>
      ) : report ? (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <PageCard className="!p-4">
              <p className="text-sm text-gray-500">Signed proposals</p>
              <p className="mt-1 text-2xl font-semibold">{report.signed.count}</p>
              <p className="text-sm text-gray-600">{formatReportMoney(report.signed.revenue)}</p>
            </PageCard>
            <PageCard className="!p-4">
              <p className="text-sm text-gray-500">Invoices (sent/paid)</p>
              <p className="mt-1 text-2xl font-semibold">{formatReportMoney(report.invoices.invoiced_revenue)}</p>
            </PageCard>
            <PageCard className="!p-4">
              <p className="text-sm text-gray-500">Paid invoices</p>
              <p className="mt-1 text-2xl font-semibold">{report.invoices.paid_count}</p>
              <p className="text-sm text-gray-600">{formatReportMoney(report.invoices.paid_revenue)}</p>
            </PageCard>
            <PageCard className="!p-4">
              <p className="text-sm text-gray-500">Outstanding</p>
              <p className="mt-1 text-2xl font-semibold">
                {formatReportMoney(report.invoices.invoiced_revenue - report.invoices.paid_revenue)}
              </p>
            </PageCard>
          </div>

          {Object.keys(report.invoices.by_status || {}).length > 0 && (
            <PageCard className="mb-6">
              <h2 className="mb-3 text-sm font-semibold text-gray-900">Invoices by status</h2>
              <div className="flex flex-wrap gap-4">
                {Object.entries(report.invoices.by_status).map(([status, row]) => (
                  <div key={status} className="rounded-lg bg-gray-50 px-4 py-3">
                    <p className="text-xs capitalize text-gray-500">{status}</p>
                    <p className="font-semibold text-gray-900">{row.count} · {formatReportMoney(row.value)}</p>
                  </div>
                ))}
              </div>
            </PageCard>
          )}

          <PageCard>
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Recent paid invoices</h2>
            {!report.recent_paid.length ? (
              <p className="text-sm text-gray-500">No paid invoices in this period.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {report.recent_paid.map((inv) => (
                  <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                    <div>
                      <Link to={`/invoices/${inv.id}`} className="font-medium text-torch-primary hover:underline">
                        {inv.invoice_number}
                      </Link>
                      <span className="ml-2 text-sm text-gray-600">{inv.company}</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      {formatReportMoney(inv.amount)}
                      {inv.paid_at && (
                        <span className="ml-2 text-gray-500">
                          {new Date(inv.paid_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </PageCard>
        </>
      ) : null}
    </Layout>
  );
};

export default RevenueReportPage;
